import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  Tabs,
  Alert,
  Popover,
} from "antd";
import { RiQuestionLine } from "react-icons/ri";
import "./historyWorkspace.css";
import { useTimeContext } from "../hooks/useTimeContext";
import { useReplayFrames } from "../hooks/useReplayFrames";
import { useHistoryAlerts } from "../hooks/useHistoryAlerts";
import { useVitalsSeries } from "../hooks/useVitalsSeries";
import { useAvailability } from "../hooks/useAvailability";
import { useFloorPlanData } from "../../FloorPlanModal/hooks/useFloorPlanData";
import { useCanvasSize } from "../../FloorPlanModal/hooks/useCanvasSize";
import { useSymbols } from "../../FloorPlanModal/hooks/useSymbols";
import { CANVAS_CARD_BODY_HEIGHT } from "../../FloorPlanModal/utils/constants";
import IndoorPositioningStations from "../../../../api/IndoorPositioningStations";
import LiveCanvas from "../../LiveDashboardModal/components/LiveCanvas";
import { useLiveTripwires } from "../../LiveDashboardModal/hooks/useLiveTripwires";
import LayerControls, {
  useMapLayers,
} from "../../LiveDashboardModal/components/LayerControls";
import OffSiteTitle from "../../LiveDashboardModal/components/OffSiteTitle";
import RangeControls from "../components/RangeControls";
import ReplayTimeline from "../components/ReplayTimeline";
import AlertHistoryTab from "../components/AlertHistoryTab";
import VitalsTab from "../components/VitalsTab";

const defaultRange = () => {
  const now = Date.now();
  return { startMs: now - 60 * 60 * 1000, endMs: now };
};

// Same register as RangeControls' 精細度 hint (a Popover behind a ? icon,
// not a banner): replay draws machines from TODAY's config, because the
// system keeps no history of a machine's name/span/alert radius. A machine
// renamed or resized since the replayed moment shows today's values, not
// what was actually configured then; a deleted one shows as 前機具 #<id>
// (the backend already fills that name in -- see buildCanvasEquipment.js).
const EQUIPMENT_HINT = (
  <div style={{ maxWidth: 280, fontSize: 13 }}>
    <p style={{ marginBottom: 0 }}>
      機具回放使用「目前」的名稱、機具長度與警戒半徑繪製，系統未保留機具設定的歷史紀錄。
      若機具在這段期間後被更名、調整尺寸，畫面會顯示今日的設定值；已刪除的機具則顯示為「前機具
      #編號」。
    </p>
  </div>
);

const EQUIPMENT_HINT_ICON = (
  <Popover
    content={EQUIPMENT_HINT}
    title="機具回放說明"
    trigger={["hover", "click"]}
  >
    <RiQuestionLine
      aria-label="機具回放說明"
      style={{
        color: "#8c8c8c",
        cursor: "help",
        verticalAlign: "middle",
      }}
    />
  </Popover>
);

export const HistoryWorkspaceCore = ({ machineID, className = "" }) => {
  const canvasContainerRef = useRef(null);
  const canvasRef = useRef(null);
  const [activeTab, setActiveTab] = useState("alerts");
  const [stationsVisible, setStationsVisible] = useState(true);
  const [symbolsVisible, setSymbolsVisible] = useState(true);
  const [equipmentVisible, setEquipmentVisible] = useState(true);
  const [tripwiresVisible, setTripwiresVisible] = useState(true);
  const [fitOffSiteKey, setFitOffSiteKey] = useState(0);
  const [visitedTabs, setVisitedTabs] = useState(() => new Set(["alerts"]));
  const [baseStations, setBaseStations] = useState([]);

  const timeContext = useTimeContext(defaultRange());
  const { range, intervalS, subjectId, coarsePlayheadMs } = timeContext;

  const floorPlan = useFloorPlanData(machineID, true);
  const {
    dimensions,
    layoutType,
    imageUrl,
    loading: floorPlanLoading,
  } = floorPlan;
  const layers = useMapLayers({ siteId: machineID, ...floorPlan });
  const canvasSize = useCanvasSize(
    dimensions,
    true,
    canvasContainerRef,
    layoutType,
    {
      fillContainer: true,
    },
  );
  const { placedSymbols } = useSymbols(machineID, true);
  const { tripwires } = useLiveTripwires(machineID, true);

  useEffect(() => {
    if (!machineID) return;
    IndoorPositioningStations.GetStations(machineID).then((response) => {
      if (response.code === 200 && Array.isArray(response.data)) {
        setBaseStations(
          response.data
            .filter((s) => s.canvas_x != null && s.canvas_y != null)
            .map((s) => ({
              ...s,
              position_meters: { x: s.canvas_x, y: s.canvas_y },
            })),
        );
      }
    });
  }, [machineID]);

  const {
    personnelAt,
    equipmentAt,
    equipmentRoster,
    error: framesError,
  } = useReplayFrames(machineID, range, intervalS, timeContext.setIntervalS);
  const {
    alerts,
    hasMore,
    loading: alertsLoading,
    loadingMore,
    loadMore,
  } = useHistoryAlerts(machineID, range);
  const { subjects: availability } = useAvailability(machineID, range);
  const { series: vitalsSeries, loading: vitalsLoading } = useVitalsSeries(
    machineID,
    subjectId,
    range,
    intervalS,
  );

  // Canvas personnel is re-derived on every coarse playhead tick (not the
  // fine one): the canvas re-renders at most 1Hz while playing, per the API
  // contract's "coarse-flush" rule.
  const canvasPersonnel = useMemo(
    () => personnelAt(coarsePlayheadMs),
    [personnelAt, coarsePlayheadMs],
  );
  const canvasEquipment = useMemo(
    () => equipmentAt(coarsePlayheadMs),
    [equipmentAt, coarsePlayheadMs],
  );

  const handleTabChange = (key) => {
    setActiveTab(key);
    setVisitedTabs((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
  };

  const handleAlertClick = (alert) => {
    timeContext.seek(alert.timestamp, { focusSubjectId: alert.personnelId });
  };

  const handleVitalsSeek = (t) => {
    timeContext.seek(t, { focusSubjectId: subjectId });
  };

  return (
    <div className={`history-workspace-core ${className}`}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <RangeControls
            range={range}
            intervalS={intervalS}
            onRangeChange={timeContext.setRange}
            onIntervalChange={timeContext.setIntervalS}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} md={16}>
          <Card
            title={
              // The frame's own offSite flags drive it, like the canvas.
              <OffSiteTitle
                title="歷史軌跡回放"
                personnel={canvasPersonnel}
                equipment={canvasEquipment}
                onFit={
                  layoutType === "plane"
                    ? () => setFitOffSiteKey((k) => k + 1)
                    : undefined
                }
              />
            }
            size="small"
            bordered
            bodyStyle={{
              padding: 12,
              position: "relative",
              height: CANVAS_CARD_BODY_HEIGHT,
            }}
          >
            {framesError && (
              <Alert
                type="warning"
                showIcon
                message={framesError}
                style={{
                  position: "absolute",
                  top: 12,
                  left: 12,
                  right: 12,
                  zIndex: 10,
                }}
              />
            )}
            <div
              ref={canvasContainerRef}
              style={{ position: "relative", width: "100%", height: "100%" }}
            >
              <LiveCanvas
                canvasRef={canvasRef}
                machineID={machineID}
                dimensions={dimensions}
                canvasSize={canvasSize}
                layoutType={layoutType}
                imageUrl={imageUrl}
                personnel={canvasPersonnel}
                equipment={canvasEquipment}
                equipmentVisible={equipmentVisible}
                // 越線 off is view-only: the alert history is untouched.
                tripwires={tripwiresVisible ? tripwires : []}
                baseStations={baseStations}
                placedSymbols={placedSymbols}
                symbolsVisible={symbolsVisible}
                stationsVisible={stationsVisible}
                selectedPersonnelId={subjectId}
                onPersonnelClick={timeContext.setSubjectId}
                loading={floorPlanLoading}
                layers={layers}
                fitOffSiteKey={fitOffSiteKey}
                layerControl={
                  <LayerControls
                    toggles={[
                      {
                        label: "基站",
                        count: baseStations.length,
                        visible: stationsVisible,
                        onToggle: () => setStationsVisible(!stationsVisible),
                      },
                      {
                        label: "物件",
                        count: placedSymbols.length,
                        visible: symbolsVisible,
                        onToggle: () => setSymbolsVisible(!symbolsVisible),
                      },
                      {
                        label: "機具",
                        count: equipmentRoster.length,
                        visible: equipmentVisible,
                        onToggle: () => setEquipmentVisible(!equipmentVisible),
                        hint: equipmentRoster.length > 0 && EQUIPMENT_HINT_ICON,
                      },
                      {
                        label: "越線",
                        count: tripwires.length,
                        visible: tripwiresVisible,
                        onToggle: () => setTripwiresVisible(!tripwiresVisible),
                      },
                    ]}
                    layers={layers}
                  />
                }
              />
            </div>
          </Card>

          <div style={{ marginTop: 16 }}>
            <ReplayTimeline
              range={range}
              finePlayheadRef={timeContext.finePlayheadRef}
              subscribe={timeContext.subscribe}
              coarsePlayheadMs={coarsePlayheadMs}
              isPlaying={timeContext.isPlaying}
              speed={timeContext.speed}
              onPlay={timeContext.play}
              onPause={timeContext.pause}
              onSpeedChange={timeContext.setSpeed}
              onScrubChange={timeContext.setFinePlayhead}
              onScrubCommit={timeContext.commitPlayhead}
              onJump={timeContext.jumpBy}
              alerts={alerts}
              onAlertMarkerClick={handleAlertClick}
              onCoverageSeek={(ms, personnelId) =>
                timeContext.seek(ms, { focusSubjectId: personnelId })
              }
              // Focus-only writer for a name click (no seek), alongside the
              // seek+focus writer above. `subjectId` is passed purely so the
              // list can RENDER the focus it already sets -- the highlight is
              // a read of the shared context and never writes back, per the
              // workspace's one-writer-per-gesture rule.
              onSubjectFocus={timeContext.setSubjectId}
              subjectId={subjectId}
              availability={availability}
            />
          </div>
        </Col>

        <Col
          xs={24}
          md={8}
          style={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            size="small"
            bordered
            className="history-tabs-card"
            style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
            bodyStyle={{
              padding: 0,
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              className="history-tabs"
              items={[
                {
                  key: "alerts",
                  label: "警報",
                  children: (
                    <div
                      style={{ padding: 12, height: "100%", overflowY: "auto" }}
                    >
                      <AlertHistoryTab
                        alerts={alerts}
                        loading={alertsLoading}
                        hasMore={hasMore}
                        loadingMore={loadingMore}
                        onLoadMore={loadMore}
                        onAlertClick={handleAlertClick}
                      />
                    </div>
                  ),
                },
                {
                  key: "vitals",
                  label: "生理",
                  // Lazy-mounted on first activation only, per spec.
                  children: visitedTabs.has("vitals") ? (
                    <div style={{ padding: 12 }}>
                      <VitalsTab
                        series={vitalsSeries}
                        loading={vitalsLoading}
                        subjectId={subjectId}
                        coarsePlayheadMs={coarsePlayheadMs}
                        onChartSeek={handleVitalsSeek}
                      />
                    </div>
                  ) : null,
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};
